import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../../../contexts/AuthContext';
import { API_CONFIG } from '../../../utils/apiConfig';

const SalesMigration = () => {
  const [migrationStatus, setMigrationStatus] = useState('idle'); // idle, migrating, completed, error
  const [migrationResult, setMigrationResult] = useState(null);
  const [unifiedSalesData, setUnifiedSalesData] = useState(null);
  const [loading, setLoading] = useState(false);
  const { currentUser } = useAuth();

  // Get Firebase ID token for API calls
  const getAuthToken = useCallback(async () => {
    if (!currentUser) {
      throw new Error('No authenticated user');
    }
    return await currentUser.getIdToken(true);
  }, [currentUser]);

  const migrateAllSales = async () => {
    setLoading(true);
    setMigrationStatus('migrating');
    
    try {
      const token = await getAuthToken();
      const response = await axios.post(`${API_CONFIG.BASE_URL}/api/sales-migration/migrate-all-sales`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setMigrationResult(response.data.data);
      setMigrationStatus('completed');
      
      // Refresh unified sales data
      await fetchUnifiedSalesData();
      
    } catch (error) {
      setMigrationStatus('error');
      setMigrationResult({ error: error.response?.data?.message || error.message });
    } finally {
      setLoading(false);
    }
  };

  const fetchUnifiedSalesData = useCallback(async () => {
    try {
      const token = await getAuthToken();
      const response = await axios.get(`${API_CONFIG.BASE_URL}/api/sales-migration/unified-sales`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      console.log('Unified sales data:', response.data);
      setUnifiedSalesData(response.data.data);
      
    } catch (error) {
      console.error('Error fetching unified sales data:', error);
    }
  }, [getAuthToken]);

  const clearSoldProducts = async () => {
    if (!window.confirm('Are you sure you want to clear all sold products? This action cannot be undone.')) {
      return;
    }
    
    setLoading(true);
    
    try {
      const token = await getAuthToken();
      const response = await axios.delete(`${API_CONFIG.BASE_URL}/api/sales-migration/clear-sold-products`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      console.log('Clear response:', response.data);
      setMigrationStatus('idle');
      setMigrationResult(null);
      setUnifiedSalesData(null);
      
    } catch (error) {
      console.error('Clear error:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUnifiedSalesData();
  }, [fetchUnifiedSalesData]);

  return (
    <div style={{ padding: '20px', maxWidth: '1200px', margin: '0 auto' }}>
      <h2>🔄 Sales Data Migration</h2>
      <p style={{ color: '#666', marginBottom: '20px' }}>
        Consolidate all sales data (cashbills, creditbills, manual entries) into a unified soldProducts collection.
      </p>

      {/* Migration Controls */}
      <div style={{ 
        backgroundColor: '#f8f9fa', 
        padding: '20px', 
        borderRadius: '8px', 
        marginBottom: '20px',
        border: '1px solid #dee2e6'
      }}>
        <h3>Migration Controls</h3>
        
        <div style={{ display: 'flex', gap: '10px', marginBottom: '15px' }}>
          <button
            onClick={migrateAllSales}
            disabled={loading || migrationStatus === 'migrating'}
            style={{
              backgroundColor: migrationStatus === 'completed' ? '#28a745' : '#007bff',
              color: 'white',
              border: 'none',
              padding: '10px 20px',
              borderRadius: '5px',
              cursor: loading ? 'not-allowed' : 'pointer',
              opacity: loading ? 0.6 : 1
            }}
          >
            {loading ? 'Migrating...' : 'Migrate All Sales Data'}
          </button>
          
          <button
            onClick={clearSoldProducts}
            disabled={loading}
            style={{
              backgroundColor: '#dc3545',
              color: 'white',
              border: 'none',
              padding: '10px 20px',
              borderRadius: '5px',
              cursor: loading ? 'not-allowed' : 'pointer',
              opacity: loading ? 0.6 : 1
            }}
          >
            Clear Sold Products
          </button>
          
          <button
            onClick={fetchUnifiedSalesData}
            disabled={loading}
            style={{
              backgroundColor: '#6c757d',
              color: 'white',
              border: 'none',
              padding: '10px 20px',
              borderRadius: '5px',
              cursor: loading ? 'not-allowed' : 'pointer',
              opacity: loading ? 0.6 : 1
            }}
          >
            Refresh Data
          </button>
        </div>

        {/* Migration Status */}
        {migrationStatus === 'migrating' && (
          <div style={{ color: '#007bff', fontWeight: 'bold' }}>
            🔄 Migration in progress... Please wait.
          </div>
        )}
        
        {migrationStatus === 'completed' && migrationResult && (
          <div style={{ color: '#28a745', fontWeight: 'bold' }}>
            ✅ Migration completed successfully!
          </div>
        )}
        
        {migrationStatus === 'error' && migrationResult?.error && (
          <div style={{ color: '#dc3545', fontWeight: 'bold' }}>
            ❌ Migration failed: {migrationResult.error}
          </div>
        )}
      </div>

      {/* Migration Results */}
      {migrationResult && migrationStatus === 'completed' && (
        <div style={{ 
          backgroundColor: '#d4edda', 
          padding: '15px', 
          borderRadius: '5px', 
          marginBottom: '20px',
          border: '1px solid #c3e6cb'
        }}>
          <h4 style={{ color: '#155724', margin: '0 0 10px 0' }}>Migration Results</h4>
          <div style={{ fontSize: '14px', color: '#155724' }}>
            <p><strong>Total Items Migrated:</strong> {migrationResult.totalMigrated}</p>
            <p><strong>Cash Bills Processed:</strong> {migrationResult.cashBillsProcessed}</p>
            <p><strong>Credit Bills Processed:</strong> {migrationResult.creditBillsProcessed}</p>
            <p><strong>Manual Entries Found:</strong> {migrationResult.manualEntriesFound}</p>
            <p><strong>Total Sold Products:</strong> {migrationResult.totalSoldProducts}</p>
            <p><strong>Migration Date:</strong> {new Date(migrationResult.migrationDate).toLocaleString()}</p>
          </div>
        </div>
      )}

      {/* Unified Sales Data */}
      {unifiedSalesData && (
        <div style={{ 
          backgroundColor: '#e3f2fd', 
          padding: '15px', 
          borderRadius: '5px', 
          marginBottom: '20px',
          border: '1px solid #bbdefb'
        }}>
          <h4 style={{ color: '#1565c0', margin: '0 0 10px 0' }}>Unified Sales Data Summary</h4>
          <div style={{ fontSize: '14px', color: '#1565c0' }}>
            <p><strong>Total Sold Products:</strong> {unifiedSalesData.summary.total}</p>
            <p><strong>Cash Bills:</strong> {unifiedSalesData.summary.cashBills}</p>
            <p><strong>Credit Bills:</strong> {unifiedSalesData.summary.creditBills}</p>
            <p><strong>Manual Entries:</strong> {unifiedSalesData.summary.manualEntries}</p>
            <p><strong>Total Sales Value:</strong> ₹{unifiedSalesData.summary.totalSalesValue?.toFixed(2) || '0.00'}</p>
            <p><strong>Last Updated:</strong> {new Date(unifiedSalesData.summary.lastUpdated).toLocaleString()}</p>
          </div>
        </div>
      )}

      {/* Sample Sold Products */}
      {unifiedSalesData && unifiedSalesData.soldProducts && unifiedSalesData.soldProducts.length > 0 && (
        <div>
          <h3>Sample Sold Products (First 10)</h3>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ 
              width: '100%', 
              borderCollapse: 'collapse', 
              fontSize: '12px',
              border: '1px solid #ddd'
            }}>
              <thead>
                <tr style={{ backgroundColor: '#f8f9fa' }}>
                  <th style={{ padding: '8px', border: '1px solid #ddd' }}>Source</th>
                  <th style={{ padding: '8px', border: '1px solid #ddd' }}>Item Code</th>
                  <th style={{ padding: '8px', border: '1px solid #ddd' }}>Item Name</th>
                  <th style={{ padding: '8px', border: '1px solid #ddd' }}>Quantity</th>
                  <th style={{ padding: '8px', border: '1px solid #ddd' }}>Unit Price</th>
                  <th style={{ padding: '8px', border: '1px solid #ddd' }}>Total Amount</th>
                  <th style={{ padding: '8px', border: '1px solid #ddd' }}>Invoice</th>
                  <th style={{ padding: '8px', border: '1px solid #ddd' }}>Customer</th>
                  <th style={{ padding: '8px', border: '1px solid #ddd' }}>Date</th>
                </tr>
              </thead>
              <tbody>
                {unifiedSalesData.soldProducts.slice(0, 10).map((product, index) => (
                  <tr key={index} style={{ backgroundColor: index % 2 === 0 ? '#f8f9fa' : 'white' }}>
                    <td style={{ padding: '8px', border: '1px solid #ddd' }}>
                      <span style={{
                        color: product.source === 'CashBill' ? 'green' : 
                               product.source === 'CreditBill' ? 'blue' : 'orange',
                        fontWeight: 'bold'
                      }}>
                        {product.source}
                      </span>
                    </td>
                    <td style={{ padding: '8px', border: '1px solid #ddd' }}>{product.itemCode}</td>
                    <td style={{ padding: '8px', border: '1px solid #ddd' }}>{product.itemName}</td>
                    <td style={{ padding: '8px', border: '1px solid #ddd' }}>{product.quantity}</td>
                    <td style={{ padding: '8px', border: '1px solid #ddd' }}>₹{product.unitPrice}</td>
                    <td style={{ padding: '8px', border: '1px solid #ddd' }}>₹{product.totalAmount}</td>
                    <td style={{ padding: '8px', border: '1px solid #ddd' }}>{product.invoiceNumber}</td>
                    <td style={{ padding: '8px', border: '1px solid #ddd' }}>{product.customerName}</td>
                    <td style={{ padding: '8px', border: '1px solid #ddd' }}>
                      {product.soldDate ? new Date(product.soldDate).toLocaleDateString() : 'N/A'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Instructions */}
      <div style={{ 
        backgroundColor: '#fff3cd', 
        padding: '15px', 
        borderRadius: '5px', 
        marginTop: '20px',
        border: '1px solid #ffeaa7'
      }}>
        <h4 style={{ color: '#856404', margin: '0 0 10px 0' }}>Instructions</h4>
        <div style={{ fontSize: '14px', color: '#856404' }}>
          <ol>
            <li><strong>Migrate All Sales Data:</strong> This will consolidate all sales from cashbills, creditbills, and manual entries into the soldProducts collection.</li>
            <li><strong>Clear Sold Products:</strong> Use this to reset the soldProducts collection if needed.</li>
            <li><strong>Refresh Data:</strong> Click this to see the current state of the unified sales data.</li>
            <li>After migration, the inventory system will use only the soldProducts collection for all calculations.</li>
          </ol>
        </div>
      </div>
    </div>
  );
};

export default SalesMigration;
